import { CompareChangeType } from "../types";

/**
 * Pill (Label) colors per change type.
 *
 * These are passed to the library `Label` via `customColor`/`textColor`, which
 * expect concrete color values (the component ignores CSS custom properties for
 * `textColor` in this build — see the `CustomTextColor` Storybook story). The
 * hex values match the design tokens (`--color-red/yellow/green-*`).
 */
export const CHANGE_STATUS_COLORS: Record<
  CompareChangeType,
  { background: string; text: string } | null
> = {
  added: { background: "#bef4ce", text: "#1b6031" }, // green-200 / green-800
  edited: { background: "#ffeda4", text: "#534200" }, // yellow-200 / yellow-900
  removed: { background: "#ffdede", text: "#8c1010" }, // red-100 / red-900
  reordered: { background: "#ffeda4", text: "#534200" }, // yellow-200 / yellow-900
  unchanged: null,
};

/**
 * Row background tint, hover background, and left accent bar per change type.
 *
 * `hoverBackground` is one step darker in the same tone (the -100 shade as a
 * literal hex — the `--color-*-100` custom properties resolve to a legacy
 * scale in the UI library, so they can't be used here).
 */
export const CHANGE_ROW_TINT: Record<
  CompareChangeType,
  { background: string; hoverBackground: string; bar: string } | null
> = {
  added: {
    background: "var(--color-green-50, #f1fcf3)",
    hoverBackground: "#defae7", // green-100
    bar: "var(--color-green-500, #2ab556)",
  },
  edited: {
    background: "var(--color-yellow-50, #fffbe8)",
    hoverBackground: "#ffeda4", // yellow-200, same as the Edited/Reordered badge
    bar: "var(--color-yellow-500, #fecf00)",
  },
  removed: {
    background: "var(--color-red-50, #fff0f0)",
    hoverBackground: "#ffdede", // red-100
    bar: "var(--color-red-500, #ff2d2d)",
  },
  reordered: {
    background: "var(--color-yellow-50, #fffbe8)",
    hoverBackground: "#ffeda4", // yellow-200, same as the Edited/Reordered badge
    bar: "var(--color-yellow-500, #fecf00)",
  },
  unchanged: null,
};

/** Hover background for rows without a change tint (Theme grey20). */
export const PLAIN_ROW_HOVER_BACKGROUND = "#f8f8f8";

export const hasStatusLabel = (changeType?: CompareChangeType): boolean =>
  !!changeType && changeType !== "unchanged";
